import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Strike from "@tiptap/extension-strike";
import {
  IconButton,
  Stack,
  Tooltip,
  Box,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import LinkIcon from "@mui/icons-material/Link";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import LinkDialog from "./link-dialog";
import EditorToolbar from "./editor-toolbar";
import { isRichTextContentEmpty } from "../../../../utils/rich-text-empty";

interface RichTextEditorProps {
  id: string;
  type: "title" | "description";
  value: any;
  onChange: (value: any) => void;
  fullBorder?: boolean;
  userPlaceholder?: string;
}

type FormatType =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "link"
  | "bulletList"
  | "orderedList";
  
const config: Record<
  "title" | "description",
  {
    placeholder: string;
    minHeight: number;
    padding: number;
    borderStyle: "underline" | "full";
    buttons: FormatType[];
    lineHeight?: number;
  }
> = {
  title: {
    placeholder: "Untitled Question",
    minHeight: 10,
    padding: 3,
    borderStyle: "underline",
    buttons: ["bold", "italic", "underline", "strike", "link"],
  },
  description: {
    placeholder: "Add optional description",
    minHeight: 96,
    padding: 10,
    lineHeight: 1.3,
    borderStyle: "full",
    buttons: [
      "bold",
      "italic",
      "underline",
      "strike",
      "link",
      "bulletList",
      "orderedList",
    ],
  },
};




export default function RichTextEditor({
  id,
  type,
  value,
  onChange,
  fullBorder = true,
  userPlaceholder
}: RichTextEditorProps) {
  const { placeholder, minHeight, padding, buttons } = useMemo(() => config[type], [type]);

  const isEmpty = isRichTextContentEmpty(value) || !value;
  
  const borderStyles = fullBorder
    ? {
        border: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: 1,
      }
    : {
        borderBottom: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: 0,
      };

  const richTextStyles = useMemo(() => {
    return  {
      ...borderStyles,
      "& .ProseMirror": {
        padding: fullBorder ? 2 : 0,
        margin: 0,
        lineHeight: 1.5,
        outline: "none",
        minHeight: `${minHeight}px`,
        maxWidth: '100%',
        overflow: 'auto',
        maxHeight: '80px',
        backgroundColor: '#fff'
      },
      "& .ProseMirror p": {
        margin: 0,
        padding: 0,
      },
      "& .ProseMirror ul, & .ProseMirror ol": {
        margin: 0,
        paddingLeft: "1.25rem",
      },
      "& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3": {
        margin: 0,
      },
    }
  }, []);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: isEmpty ? `<p>${userPlaceholder ?? placeholder}</p>` : value, 
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
  });



  useEffect(() => {
    if (editor) {
      const contentToSet = isEmpty ? `<p>${userPlaceholder ?? placeholder}</p>` : value;
      if (JSON.stringify(editor.getJSON()) !== JSON.stringify(contentToSet)) {
        editor.commands.setContent(contentToSet, false);
      }
    }
  }, [id]);


  const openLinkDialog = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");

    setLinkText(selectedText || "");
    setLinkUrl(editor.getAttributes("link").href || "");
    setLinkDialogOpen(true);
  };

  const insertLink = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;

    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContentAt(from, linkText)
      .setTextSelection({ from, to: from + linkText.length })
      .setLink({ href: linkUrl })
      .run();

    setLinkDialogOpen(false);
    setLinkText("");
    setLinkUrl("");
  };

  const commandMap: Record<FormatType, ((chain: ReturnType<Editor['chain']>) => boolean) | (() => void)> = useMemo(() => ({
    bold: (chain) => chain.toggleBold().run(),
    italic: (chain) => chain.toggleItalic().run(),
    underline: (chain) => chain.toggleUnderline().run(),
    strike: (chain) => chain.toggleStrike().run(),
    link: () => openLinkDialog(),
    bulletList: (chain) => chain.toggleBulletList().run(),
    orderedList: (chain) => chain.toggleOrderedList().run(),
  }), [openLinkDialog]);
  
  
  const toggleFormat = (format: FormatType) => {
    if (!editor) return;
  
    const chain = editor.chain().focus();
    const command = commandMap[format];
  
    if (typeof command === "function") {
      if (format === "link") {
        (command as () => void)(); 
      } else {
        (command as (chain: ReturnType<Editor["chain"]>) => boolean)(chain);
      }
    }
  };
  
  

  return (
    <Stack spacing={1}>
      <Box sx={richTextStyles}>
        <EditorContent editor={editor} />
      </Box>

      <EditorToolbar editor={editor} buttons={buttons} onToggle={toggleFormat} />

      <LinkDialog
        open={linkDialogOpen}
        linkText={linkText}
        linkUrl={linkUrl}
        onClose={() => setLinkDialogOpen(false)}
        onChangeLinkText={setLinkText}
        onChangeLinkUrl={setLinkUrl}
        onInsert={insertLink}
        insertDisabled={!linkText || !linkUrl}
      />

      </Stack>
  );
}
